// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngSticky from "@eisinc/App/Components/XngSticky";
import { DivWrapper } from "@eisinc/XDL/constants";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, mergeWithCss, xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ContentWrapper } from "@insite/content-library/Widgets/Basic/RichContent";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useMemo, useState } from "react";
import { css } from "styled-components";

interface CartPageTemplateProps {
    children: React.ReactNode;
    extendedStyles?: CartPageTemplateStyles;
    isCheckout?: boolean;
    currentView?: string;
}

interface Props {
    MainSection: React.FC<any>;
    HeaderSection: React.FC<any>;
    TitleSection: React.FC<any>;
    CartApproverMessage: React.FC<any>;
    CartFulfillmentSelector: React.FC<any>;
    CartTotalSection: React.FC<any>;
    ActionsSection: React.FC<any>;
}

interface CartPageTemplateContextProps {
    styles: CartPageTemplateStyles;
    isCheckout?: boolean;
    currentView?: string;
}

export interface CartPageTemplateStyles {
    container?: InjectableCss;
    headerSection?: InjectableCss;
    titleSection?: InjectableCss;
    mainSection?: InjectableCss;
    cartApproverMessage?: InjectableCss;
    cartFulfillmentSelector?: InjectableCss;
    totalSection?: InjectableCss;
    actionsSection?: InjectableCss;
    xngStickyStyles?: InjectableCss;
}

const CartPageTemplateContext = React.createContext<CartPageTemplateContextProps>({
    styles: {},
    isCheckout: false,
    currentView: "default",
});

export const useCartPageTemplateContext = () => {
    const context = React.useContext(CartPageTemplateContext);
    if (!context) {
        throw new Error("CartPageTemplate compound components cannot be rendered outside CartPageTemplate");
    }
    return context;
};

export const cartPageTemplateStyles: CartPageTemplateStyles = {
    container: {
        css: css`
            display: grid;
            grid-template-columns: minmax(0, 1fr) minmax(0, 317px);
            grid-column-gap: 22px;

            ${xScreen("lte", "md")} {
                grid-template-columns: minmax(0, 50%) minmax(0, 50%);
                grid-column-gap: 20px;
            }

            ${xScreen("lte", "sm")} {
                grid-template-columns: minmax(0, 328px) minmax(0, 1fr);
            }

            + .cart-page-template {
                border-top: 1px solid ${color("common.border")};
                padding-top: 48px;
                margin-top: 24px;

                ${xScreen("lte", "sm")} {
                    border-top: 0;
                    margin-top: 0;
                }
            }
        `,
    },

    headerSection: {
        css: css`
            grid-row: 1 / 2;

            ${xScreen("lte", "sm")} {
                grid-column: 1 / 3;
            }
            @media print {
                width: 100%;
            }
        `,
    },
    titleSection: {
        css: css`
            width: 100%;
            margin-bottom: 16px;

            ${xScreen("lte", "sm")} {
                display: flex;
                flex-direction: column;
                row-gap: 16px;
                margin-bottom: 24px;
            }
        `,
    },
    actionsSection: {
        css: css`
            width: 100%;
            margin-bottom: 12px;

            &:has(${DivWrapper}:empty) {
                display: none;
            }
        `,
    },
    mainSection: {
        css: css`
            grid-column: 1 / 2;

            ${xScreen("lte", "sm")} {
                grid-column: 1 / 3;
                padding: 0;
                margin-top: 0;
                width: 100%;
                margin-bottom: 0;
            }

            @media print {
                float: left;
                display: inline-block;
                width: 100%;
                grid-column: 1/3;
            }
        `,
    },

    totalSection: {
        css: css`
            grid-row: 1 / 4;
            grid-column: 2 / 3;
            // XNG-Sticky
            & > div.xng-sticky > ${DivWrapper} > div:not(:last-child) {
                margin-bottom: 24px;
            }

            display: flex;
            flex-direction: column;
            row-gap: 24px;

            ${xScreen("lte", "sm")} {
                grid-row: 3 / 3;
                grid-column: 1 / 3;
                align-items: center;
                margin-top: 48px;
                width: 100%;

                & > div.xng-sticky > ${DivWrapper} {
                    height: auto;
                }

                & > div.xng-sticky {
                    max-width: 300px;
                }
            }

            ${xScreen("lte", "sm")} {
                & > div.xng-sticky {
                    max-width: 100%;
                }
            }

            @media print {
                float: right;
                display: inline-block;
                width: calc(33.33% - 22px);
                margin-top: 48px;
                & > div.xng-sticky {
                    position: static;
                }
            }
        `,
    },
    cartApproverMessage: {
        css: css`
            grid-row: 2 / 3;
            grid-column: 1 / 2;
            display: flex;
            flex-direction: column;
            gap: 12px;
            padding-bottom: 16px;
            border-bottom: 1px solid ${color("common.border")};
            margin-bottom: 16px;

            ${xScreen("lte", "sm")} {
                grid-column: 1 / 3;
            }

            & > ${DivWrapper} {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            &:has(${DivWrapper}:empty) {
                display: none;
            }
            &:has(${DivWrapper} > div:only-child:empty) {
                display: none;
            }

            & ${ContentWrapper} > ${TypographyStyle} {
                font-size: ${fontConfig.singleLineSmall.size}px;
                line-height: ${fontConfig.singleLineSmall.lineHeight};
                margin-bottom: 12px;
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
    cartFulfillmentSelector: {
        css: css`
            margin-bottom: 24px;
            &:has(${DivWrapper}:empty) {
                display: none;
            }
        `,
    },
    xngStickyStyles: {
        css: css`
            display: flex;
            flex-direction: column;
            row-gap: 24px;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
};

const CartPageTemplate: FC<CartPageTemplateProps> & Props = ({ children, extendedStyles, isCheckout, currentView }) => {
    const [styles] = useState(() => mergeWithCss(cartPageTemplateStyles, extendedStyles));
    const value = useMemo(() => ({ styles, isCheckout, currentView }), [styles, isCheckout, currentView]);
    return (
        <CartPageTemplateContext.Provider value={value}>
            <StyledWrapper {...styles.container} className="cart-page-template" {...{ currentView }}>
                {children}
            </StyledWrapper>
        </CartPageTemplateContext.Provider>
    );
};

const HeaderSection: FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.headerSection}>{children}</StyledWrapper>;
};

const TitleSection: FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.titleSection}>{children}</StyledWrapper>;
};

const CartApproverMessage: FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.cartApproverMessage}>{children}</StyledWrapper>;
};
const CartFulfillmentSelector: FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.cartFulfillmentSelector}>{children}</StyledWrapper>;
};
const MainSection: FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const { styles, isCheckout, currentView } = useCartPageTemplateContext();
    return (
        <StyledWrapper {...styles.mainSection} {...{ isCheckout, currentView }}>
            {children}
        </StyledWrapper>
    );
};

const CartTotalSection: FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const { styles, isCheckout, currentView } = useCartPageTemplateContext();

    return (
        <StyledWrapper {...styles.totalSection} {...{ isCheckout, currentView }}>
            <XngSticky {...styles.xngStickyStyles}>{children}</XngSticky>
        </StyledWrapper>
    );
};

const ActionsSection: FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return (
        <StyledWrapper {...styles.actionsSection} className="hideForPrint">
            {children}
        </StyledWrapper>
    );
};

CartPageTemplate.HeaderSection = HeaderSection;
CartPageTemplate.MainSection = MainSection;
CartPageTemplate.TitleSection = TitleSection;
CartPageTemplate.CartApproverMessage = CartApproverMessage;
CartPageTemplate.CartFulfillmentSelector = CartFulfillmentSelector;
CartPageTemplate.CartTotalSection = CartTotalSection;
CartPageTemplate.ActionsSection = ActionsSection;

export default CartPageTemplate;
